import { z } from 'zod';
export const contentTypes = [
    'ofertas',
    'noticias',
    'faqs',
    'banners',
    'hero-slides',
    'partners',
];
export const contentTypeSchema = z.enum(contentTypes);
export const contentStatuses = ['draft', 'scheduled', 'published', 'archived'];
export const contentStatusSchema = z.enum(contentStatuses);
export const effectiveVisibilities = ['live', 'scheduled', 'expired', 'draft', 'archived'];
export const effectiveVisibilitySchema = z.enum(effectiveVisibilities);
export const contentDisplayTimeZone = 'America/Puerto_Rico';
/** Boundary timestamps are instants, not local wall-clock values or Date objects. */
export const utcInstantSchema = z.iso
    .datetime({ offset: false })
    .refine((value) => value.endsWith('Z'), 'Must be an ISO 8601 UTC instant ending in Z');
export const slugSchema = z
    .string()
    .min(1)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Must be a lowercase URL slug');
export const seoPathSchema = z
    .string()
    .regex(/^\/(?:[a-z0-9]+(?:-[a-z0-9]+)*(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)*)?$/, 'Must be an absolute site path without an origin, query, fragment, traversal, or trailing slash');
const hasSafeAbsoluteHttpsUrl = (value) => {
    try {
        const url = new URL(value);
        return url.protocol === 'https:' && url.username === '' && url.password === '';
    }
    catch {
        return false;
    }
};
const isSafeSiteRelativeUrl = (value) => {
    if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\'))
        return false;
    try {
        const url = new URL(value, 'https://relative.apuestapr.invalid');
        return (url.origin === 'https://relative.apuestapr.invalid' &&
            !url.pathname.split('/').includes('..') &&
            url.username === '' &&
            url.password === '');
    }
    catch {
        return false;
    }
};
/** CTA and partner links may be HTTPS absolute URLs or one-slash site-relative URLs. */
export const safeLinkSchema = z
    .string()
    .min(1)
    .refine((value) => isSafeSiteRelativeUrl(value) || hasSafeAbsoluteHttpsUrl(value), 'Must be an HTTPS URL without credentials or a site-relative path beginning with one slash');
/** The migration allowlist. Task 11 validates configured additions against the same policy. */
export const defaultContentImageOrigins = [
    'https://dashboard.apuestapr.com',
    'https://images.apuestapr.com',
];
export const mediaPathSchema = z
    .string()
    .regex(/^\/media\/(?!.*(?:^|\/)\.\.(?:\/|$))[^?#\\]+$/, 'Must be a repository /media/ path');
export const imageReferenceSchema = z.string().superRefine((value, context) => {
    if (mediaPathSchema.safeParse(value).success)
        return;
    if (!hasSafeAbsoluteHttpsUrl(value)) {
        context.addIssue({
            code: 'custom',
            message: 'Must be a repository /media/ path or an HTTPS image URL without credentials',
        });
        return;
    }
    const origin = new URL(value).origin;
    if (!defaultContentImageOrigins.includes(origin)) {
        context.addIssue({ code: 'custom', message: 'Image origin is not allowed' });
    }
});
export const publishWindowShape = {
    status: contentStatusSchema,
    publishAt: utcInstantSchema.nullable(),
    unpublishAt: utcInstantSchema.nullable(),
};
const _partialPublishWindowSchema = z.object(publishWindowShape).partial();
export const addPublishWindowIssues = (value, context) => {
    if (value.status === 'scheduled' && (value.publishAt === null || value.publishAt === undefined)) {
        context.addIssue({
            code: 'custom',
            path: ['publishAt'],
            message: 'A scheduled item requires a publication time',
        });
    }
    if (value.publishAt !== null &&
        value.publishAt !== undefined &&
        value.unpublishAt !== null &&
        value.unpublishAt !== undefined &&
        Date.parse(value.unpublishAt) <= Date.parse(value.publishAt)) {
        const message = 'Unpublication time must be strictly later than publication time';
        context.addIssue({ code: 'custom', path: ['publishAt'], message });
        context.addIssue({ code: 'custom', path: ['unpublishAt'], message });
    }
};
export const publishWindowSchema = z.object(publishWindowShape).superRefine(addPublishWindowIssues);
export const contentStatusChangeSchema = publishWindowSchema;
const _ctaPairValueSchema = z.object({
    ctaLabel: z.string().nullable().optional(),
    ctaUrl: z.string().nullable().optional(),
});
export const addCtaPairIssues = (value, context) => {
    const labelPresent = value.ctaLabel !== null && value.ctaLabel !== undefined;
    const urlPresent = value.ctaUrl !== null && value.ctaUrl !== undefined;
    if (labelPresent === urlPresent)
        return;
    const message = 'CTA label and URL must both be present or both be null';
    context.addIssue({ code: 'custom', path: ['ctaLabel'], message });
    context.addIssue({ code: 'custom', path: ['ctaUrl'], message });
};
export const editorSummarySchema = z.object({
    id: z.number().int().positive(),
    nombre: z.string().min(1),
});
export const adminMetadataSchema = z.object({
    effectiveVisibility: effectiveVisibilitySchema,
    createdAt: utcInstantSchema,
    updatedAt: utcInstantSchema,
    updatedBy: editorSummarySchema.nullable(),
});
export const deleteResultSchema = z.object({ deleted: z.literal(true) });
export const nonEmptyUpdate = (schema) => schema.partial().refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be supplied',
    path: [],
});
//# sourceMappingURL=common.js.map